import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { usePage, useAreas, useArticles, formatDate } from "@/lib/content";
import { Reveal, MaskedLines, Marquee, EASE } from "@/components/site/Motion";
import { Overline, Btn, SectionHead, CTABand, usePageTitle } from "@/components/site/Bits";

function HeroPhoto({ src, name }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  return (
    <div ref={ref} className="relative">
      <div className="absolute -inset-3 rounded-t-[220px] border border-gold/25 md:-inset-4" aria-hidden="true" />
      <motion.div style={reduce ? {} : { y }} className="relative overflow-hidden rounded-t-[220px] border border-gold/40 bg-navy-2">
        <img
          src={src}
          alt={name}
          data-testid="hero-photo"
          className="aspect-[3/4] w-full object-cover object-top"
        />
      </motion.div>
      <div className="absolute -left-4 bottom-10 hidden bg-ivory px-6 py-4 shadow-xl md:block lg:-left-12">
        <span className="gold-rule block w-10" aria-hidden="true" />
        <p className="mt-2 font-display text-lg text-navy">Dra. Fernanda Cotan</p>
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold-deep">Assessoria Jurídica</p>
      </div>
    </div>
  );
}

export default function Home() {
  usePageTitle(null);
  const { sections: s } = usePage("home");
  const { sections: servSections } = usePage("servicos");
  const { sections: pqSections } = usePage("para_quem");
  const areas = useAreas();
  const { items: articles } = useArticles(3);
  const marqueeWords = (s.marquee_words || "").split(";").map((w) => w.trim()).filter(Boolean);
  const servicos = Array.isArray(servSections.items) ? servSections.items : [];
  const publicos = Array.isArray(pqSections.items) ? pqSections.items : [];

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-ivory grain">
        <span className="pointer-events-none absolute -right-16 bottom-0 select-none font-display text-[34vw] italic leading-none text-ivory/[0.025]" aria-hidden="true">
          SC
        </span>
        <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-32 md:px-8 md:pt-40 lg:grid-cols-12 lg:gap-10 lg:pb-28">
          <div className="flex flex-col justify-center lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: EASE }}>
              <Overline light>{s.hero_eyebrow || "SILVA COTAN E ASSOCIADOS — ASSESSORIA JURÍDICA"}</Overline>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.35 }}
              className="mt-7 text-xs font-bold uppercase tracking-[0.3em] text-gold md:text-sm"
            >
              {s.hero_tagline}
            </motion.p>
            <h1 className="mt-4 max-w-2xl font-display text-[2.6rem] leading-[1.06] md:text-6xl lg:text-[4.2rem]" data-testid="hero-title">
              <MaskedLines text={s.hero_title || "Segurança jurídica para decisões que importam."} delay={0.45} lines={3} className="block" />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
              className="mt-7 max-w-xl text-base leading-relaxed text-ivory/70"
            >
              {s.hero_text}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Btn to="/areas-de-atuacao" variant="solid" testid="cta-home-areas">{s.cta_areas || "Conheça nossas áreas de atuação"}</Btn>
              <Btn to="/contato" variant="outline-light" testid="cta-home-contato">{s.cta_contato || "Entre em contato"}</Btn>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.35 }}
              className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:gap-10"
            >
              {[
                { label: s.cta_servicos || "Conheça nossos serviços", to: "/servicos", tid: "cta-home-servicos" },
                { label: s.cta_conteudos || "Acesse nossos conteúdos", to: "/conteudo-juridico", tid: "cta-home-conteudos" },
              ].map((l) => (
                <Link key={l.to} to={l.to} data-testid={l.tid} className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-gold">
                  {l.label}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="relative lg:col-span-5"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6, ease: EASE }}
          >
            <HeroPhoto src={s.hero_image || "/media/dra-sentada.jpg"} name="Dra. Fernanda Cotan, advogada da Silva Cotan" />
          </motion.div>
        </div>

        {marqueeWords.length > 0 && (
          <div className="border-t border-ivory/10 py-6 md:py-8">
            <Marquee items={marqueeWords} />
          </div>
        )}
      </section>

      {/* SOBRE */}
      <section className="bg-ivory">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-2">
          <Reveal className="relative">
            <div className="absolute -left-3 -top-3 h-24 w-24 border-l-2 border-t-2 border-gold" aria-hidden="true" />
            <img
              src={s.sobre_image || "/media/dra-mesa.jpg"}
              alt="Dra. Fernanda Cotan em atuação no escritório Silva Cotan"
              className="relative aspect-[4/3] w-full object-cover object-top"
              data-testid="home-sobre-image"
            />
            <div className="absolute -bottom-3 -right-3 h-24 w-24 border-b-2 border-r-2 border-gold" aria-hidden="true" />
          </Reveal>
          <div>
            <SectionHead num="01" title={s.sobre_title || "Assessoria jurídica moderna, estruturada e confiável"} />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-inkgray">{s.sobre_text}</p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-8">
                <Btn to="/quem-somos" variant="outline-dark" testid="home-quem-somos-button">Quem Somos</Btn>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ÁREAS */}
      <section className="bg-paper">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <SectionHead num="02" title={s.areas_title || "Áreas de Atuação"} text={s.areas_text} />
            <Reveal delay={0.2}>
              <Btn to="/areas-de-atuacao" variant="outline-dark" testid="home-areas-all-button">Ver todas</Btn>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {areas.map((area, i) => (
              <Reveal key={area.slug} delay={0.08 * i}>
                <Link
                  to={`/areas-de-atuacao/${area.slug}`}
                  data-testid={`home-area-card-${area.slug}`}
                  className="group flex h-full items-start justify-between gap-6 border border-navy/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold md:p-10"
                >
                  <div>
                    <span className="font-display text-sm italic text-gold">0{area.order || i + 1}</span>
                    <h3 className="mt-3 font-display text-2xl text-navy transition-colors group-hover:text-gold-deep md:text-3xl">{area.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-inkgray">{area.summary}</p>
                  </div>
                  <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center border border-navy/15 text-navy transition-all duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-navy">
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section className="bg-navy text-ivory grain">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <SectionHead light num="03" title={s.servicos_title || "Serviços Jurídicos"} text={s.servicos_text} />
            <Reveal delay={0.2}>
              <Btn to="/servicos" variant="outline-light" testid="home-servicos-all-button">Ver serviços</Btn>
            </Reveal>
          </div>
          <div className="mt-14">
            {servicos.map((item, i) => <ServiceRow key={i} index={i} item={item} />)}
          </div>
        </div>
      </section>

      {/* PARA QUEM */}
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <SectionHead num="04" title={s.publicos_title || "Para Quem Atuamos"} text={s.publicos_text} />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {publicos.map((p, i) => (
              <Reveal key={p.title || i} delay={0.06 * i}>
                <div className="flex h-full flex-col justify-between border border-navy/10 bg-white px-8 py-7 transition-colors duration-300 hover:border-gold">
                  <p className="font-display text-xl text-navy">{p.title}</p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-gold-deep">Suporte dedicado</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link to="/para-quem-atuamos" data-testid="home-publicos-link" className="flex h-full min-h-[120px] items-center justify-center gap-3 bg-navy px-8 py-7 text-xs font-bold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-navy-2">
                Para quem atuamos <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTEÚDOS */}
      <section className="bg-paper">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <SectionHead num="05" title={s.conteudos_title || "Conteúdo Jurídico"} text={s.conteudos_text} />
            <Reveal delay={0.2}>
              <Btn to="/conteudo-juridico" variant="outline-dark" testid="home-conteudos-all-button">Todos os conteúdos</Btn>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {articles.length === 0 && (
              <Reveal>
                <p className="border border-dashed border-navy/20 px-8 py-10 text-sm text-inkgray">
                  Em breve, novos conteúdos serão publicados por aqui.
                </p>
              </Reveal>
            )}
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={0.08 * i}>
                <Link to={`/conteudo-juridico/${a.slug}`} data-testid={`home-article-card-${a.slug}`} className="group block h-full border border-navy/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-gold">
                  {a.cover_url ? (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={a.cover_url} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] overflow-hidden bg-navy">
                      <img src="/media/capa-padrao.jpg" alt="" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-[1.03]" />
                    </div>
                  )}
                  <div className="p-7">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gold-deep">
                      {a.category || "Conteúdo"} · {formatDate(a.published_at || a.created_at)}
                    </p>
                    <h3 className="mt-3 font-display text-xl leading-snug text-navy group-hover:text-gold-deep">{a.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title={s.band_title || "Estamos prontos para te apoiar."} button={s.band_button || "Fale conosco"} />
    </>
  );
}

function ServiceRow({ index, item }) {
  if (!item) return null;
  return (
    <Reveal delay={0.05 * index}>
      <Link to="/servicos" data-testid={`home-service-row-${index}`} className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-t border-ivory/10 py-7 transition-colors duration-300 hover:border-gold/50 md:grid-cols-[80px_1fr_40px] md:gap-10 md:py-9">
        <span className="font-display text-lg italic text-gold">0{index + 1}</span>
        <div>
          <h3 className="font-display text-xl text-ivory transition-colors group-hover:text-gold md:text-2xl">{item.title}</h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ivory/55">{item.text}</p>
        </div>
        <ArrowRight size={18} className="hidden text-gold transition-transform duration-300 group-hover:translate-x-1 md:block" />
      </Link>
    </Reveal>
  );
}
